import clsx from 'clsx';
import React, { useRef, useEffect } from 'react';
import Icon from './Icon';

const Dialog = ({
  isOpen,
  onClose,
  children,
  headerContent,
  hideHeader,
  closeButtonAriaLabel,
  disableCloseOnOutsideClick,
}) => {
  const dialogRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) return;

    const focusableElements = dialog.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement =
      focusableElements[focusableElements.length - 1];

    if (isOpen) {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      dialog.showModal();
      document.body.classList.add('dialog--open');
      // showModal() focuses the first focusable element inside the dialog,
      // which makes the browser scroll the dialog's DOM position into view
      // even though the top-layer renders it centered. Restore the scroll
      // position so the page underneath stays put.
      if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
        window.scrollTo(scrollX, scrollY);
      }
    } else {
      dialog.close();
      document.body.classList.remove('dialog--open');
    }

    const lockFocusToModal = (event) => {
      if (firstFocusableElement === null || lastFocusableElement === null)
        return;
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusableElement) {
            lastFocusableElement.focus();
            event.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusableElement) {
            firstFocusableElement.focus();
            event.preventDefault();
          }
        }
      }
    };

    const handleEscKeyPress = (event) => {
      if (event.key === 'Escape') {
        if (disableCloseOnOutsideClick) {
          event.preventDefault();
          return;
        }
        onClose?.();
      }
    };

    const handleOutsideClick = (event) => {
      if (disableCloseOnOutsideClick) return;
      if (contentRef.current && !contentRef.current.contains(event.target)) {
        onClose?.();
      }
    };

    dialog.addEventListener('click', handleOutsideClick);
    dialog.addEventListener('keydown', lockFocusToModal);
    dialog.addEventListener('keydown', handleEscKeyPress);

    return () => {
      dialog.removeEventListener('click', handleOutsideClick);
      dialog.removeEventListener('keydown', lockFocusToModal);
      dialog.removeEventListener('keydown', handleEscKeyPress);
    };
  }, [disableCloseOnOutsideClick, isOpen, onClose]);

  return (
    <dialog className="dialog" ref={dialogRef} onClose={onClose}>
      <div ref={contentRef}>
        {!hideHeader && (
          <DialogHeader
            headerContent={headerContent}
            onClose={onClose}
            closeButtonAriaLabel={closeButtonAriaLabel}
          />
        )}
        <DialogContent removePaddingTop={!hideHeader}>{children}</DialogContent>
      </div>
    </dialog>
  );
};

const DialogContent = ({ children, removePaddingTop }) => {
  return (
    <div
      className={clsx(
        'dialog__content',
        removePaddingTop && 'dialog__content--pt-none'
      )}
    >
      {children}
    </div>
  );
};

const DialogHeader = ({ headerContent, onClose, closeButtonAriaLabel }) => {
  return (
    <div
      className={clsx(
        'dialog__header',
        headerContent
          ? 'dialog__header--with-content'
          : 'dialog__header--no-content'
      )}
    >
      {headerContent}
      <button
        aria-label={closeButtonAriaLabel}
        className="dialog__close-button"
        onClick={onClose}
      >
        <Icon name="Close" size={24} className="dialog__close-button-icon" />
      </button>
    </div>
  );
};

export default Dialog;
